/**
 * WorkspaceDemoEntryPage (`/demo/workspace`) — Patch 34.1.10.
 *
 * Guest-safe entry into the fully seeded ``demo`` workspace. Calls the
 * public-preview auto-login endpoint (issues a real session for
 * ``demo@orkexra.com``, gated on ``PUBLIC_PREVIEW_MODE``), then
 * navigates to ``/home`` where the DemoBanner + preloaded walkthrough
 * render.
 *
 * Security invariants (Patches 34.1.6 / 34.1.9): the demo user has
 * ``is_dev_user=False`` so AI generation is blocked (403), exports are
 * blocked (403 ``export_requires_admin``), and provider / model / cost
 * / prompt lineage / workspace / campaign_pack IDs are redacted from
 * every API response.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { demoLogin } from "@/entities/auth/api";
import { useAuthStore } from "@/entities/auth/store";
import { setAuthToken } from "@/shared/api/http";
import { trackDemoEvent } from "@/shared/lib/demoAnalytics";

export default function WorkspaceDemoEntryPage(): JSX.Element {
  const token = useAuthStore((s) => s.accessToken);
  const setAuth = useAuthStore((s) => s.setAuth);
  const [phase, setPhase] = useState<"logging_in" | "ready" | "error">("logging_in");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    // Already signed in — skip the round-trip.
    if (token) {
      setPhase("ready");
      return;
    }
    trackDemoEvent("seeded_demo_opened", "/demo/workspace");
    let cancelled = false;
    (async () => {
      try {
        const t = await demoLogin();
        if (cancelled) return;
        setAuth(t);
        setAuthToken(t.access_token);
        setPhase("ready");
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof Error ? err.message : "Failed to open demo.");
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, setAuth]);

  if (phase === "ready") return <Navigate to="/home" replace />;

  if (phase === "error") {
    return (
      <div
        data-testid="workspace-demo-error"
        className="flex min-h-screen items-center justify-center bg-[var(--md-ink-0)] px-6 text-center text-[var(--md-ivory-0)]"
      >
        <div className="max-w-md">
          <div className="md-eyebrow md-eyebrow--amber">Demo unavailable</div>
          <h1 className="mt-3 font-display italic text-2xl">
            We couldn&apos;t open the workspace demo just now.
          </h1>
          <p className="mt-3 text-sm text-[var(--md-fog)]">{errorMessage}</p>
          <a href="/demo" className="md-btn md-btn--primary mt-6 inline-flex">
            Choose another demo
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="workspace-demo-splash"
      className="flex min-h-screen items-center justify-center bg-[var(--md-ink-0)] text-[var(--md-ivory-0)]"
    >
      <div className="flex items-center gap-3 md-eyebrow">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--md-amber)]" />
        Preparing your ready-to-work workspace…
      </div>
    </div>
  );
}
